// Focusable selectors based on WAI-ARIA practices
const DEFAULT_FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

function isElementVisible(el) {
    if (!el) return false;
    // Skip elements intentionally hidden or inert
    if (el.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // If element has no layout box and is not position:fixed => hidden
    if (el.offsetParent === null && style.position !== 'fixed') return false;
    return true;
}

function findLast(arr, predicate) {
    for (let i = arr.length - 1; i >= 0; i -= 1) {
        if (predicate(arr[i])) return arr[i];
    }
    return undefined;
}

export default class FocusableElements {
    /**
     * @param {HTMLElement|Document} context - root container to search.
     * @param {string} trapClassName - CSS class used for focus-trap sentinels.
     * @param {string} selector - custom focusable selector.
     */
    constructor(context = document, trapClassName = 'focus-trap', selector = DEFAULT_FOCUSABLE_SELECTOR) {
        this.context = context;
        this.trapClassName = trapClassName;
        this.selector = selector;
        this.elements = [];
        this.refresh();
    }

    /** Re-scan DOM under current context. */
    refresh() {
        const nodes = Array.from(this.context.querySelectorAll(this.selector));
        this.elements = nodes.filter((el) => isElementVisible(el) && !el.hasAttribute('disabled'));
        return this;
    }

    get first() {
        return this.elements[0];
    }

    get firstReal() {
        return this.elements.find((el) => !el.classList.contains(this.trapClassName));
    }

    get last() {
        return this.elements[this.elements.length - 1];
    }

    get lastReal() {
        return findLast(this.elements, (el) => !el.classList.contains(this.trapClassName));
    }

    get all() {
        return this.elements.slice();
    }
}
